import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { IngestFile } from '@/redux/slices/slice_types';
import { darken, Fade, Typography } from '@mui/material';
import PolarisIcon from '../_common/PolarisIcon';
import PosterDisplay from './PosterDisplay';
import AudioPlayer from '../_common/AudioPlayer';
import { ConfigContext } from '@/contextAPI/configContext';
import { axios, checkIfUrlIsValid } from '@/_utils/utils';
import { useToast } from '../_common/Toast';

interface IngestMangementPreviewProps {
  data: IngestFile;
  expanded: boolean;
  previewDimensions: { width: number; height: number };
  isGrid?: boolean;
}

export const previewEnabledTypes = ['video', 'audio', 'image'];

const IngestMangementPreview = ({ data, expanded, previewDimensions, isGrid }: IngestMangementPreviewProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const playbackEnabled = data.file_type === 'video' || data.file_type === 'audio' || data.file_type === 'image';
  const [showPlayer, setShowPlayer] = useState(false);
  const [urlToUse, setUrlToUse] = useState<string>('');
  const { theme } = useContext(ConfigContext);
  const fetchSignedUrl = useContext(ConfigContext).ingestManagementConfig?.fetchSignedUrl;
  const [signedUrl, setSignedURL] = useState<string | null>(data.media_url || null);
  const { error } = useToast();

  useEffect(() => {
    const retrieveThumbnail = async () => {
      let workingUrl = null;

      const fallbackUrls = [signedUrl, data?.media_url, theme?.preview_placeholder, theme?.logo];

      if (data?.thumbnail_url) {
        fallbackUrls.unshift(data?.thumbnail_url);
      }

      for (const url of fallbackUrls) {
        if (url) {
          const isValid = await checkIfUrlIsValid(url);
          if (isValid) {
            workingUrl = url;
            break;
          }
        }
      }

      setUrlToUse(workingUrl);
    };

    retrieveThumbnail();
  }, [data, signedUrl]);

  useEffect(() => {
    if (wrapperRef.current) {
      setDimensions({
        height: wrapperRef.current.clientHeight,
        width: wrapperRef.current.clientWidth
      });
    }
  }, [wrapperRef.current]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    if (expanded || isGrid) {
      setDimensions({
        height: previewDimensions?.height,
        width: previewDimensions?.width - (!isGrid ? 1 : 0)
      });
    } else {
      setDimensions({
        height: wrapperRef.current.clientHeight,
        width: wrapperRef.current.clientWidth
      });
    }
  }, [expanded, wrapperRef.current, previewDimensions]);

  useEffect(() => {
    if (expanded && playbackEnabled) {
      setShowPlayer(true);
      if (fetchSignedUrl) {
        axios
          .get(`/ingest_manager/ingest_records/${data.id}/media/`)
          .then((res) => {
            if (res.data?.data?.media_url) setSignedURL(res.data.data.media_url);
          })
          .catch(() => error('Failed to fetch signed URL'));
      }
    } else {
      setShowPlayer(false);
    }
  }, [expanded]);

  const getPlayer = () => {
    switch (data.file_type) {
      case 'video':
        return <PosterDisplay url={urlToUse as string} filename={data?.filename} height={isGrid ? '100%' : previewDimensions?.height} />;
      case 'image':
        return <PosterDisplay url={signedUrl || ''} filename={data?.filename} height={isGrid ? '100%' : previewDimensions?.height} />;
      case 'audio': {
        return <StyledAudioPlayer url={signedUrl || ''} height={dimensions?.height} />;
      }
      default:
        return <Typography variant="body1">No Player Available</Typography>;
    }
  };

  const getMainIconProps = () => {
    switch (data.file_type) {
      case 'audio':
        return {
          icon: 'ingest_audio',
          height: isGrid ? '45%' : 34,
          width: isGrid ? '45%' : 34
        };
      case 'subtitle':
        return {
          icon: 'ingest_subtitle',
          height: isGrid ? '55%' : 40,
          width: isGrid ? '55%' : 40
        };
      default:
        return {
          icon: 'ingest_doc',
          height: isGrid ? '45%' : 34,
          width: isGrid ? '45%' : 34
        };
    }
  };

  const getLowerRightIconProps = () => {
    switch (data.file_type) {
      case 'video':
        return {
          icon: 'thumbnail',
          height: isGrid ? 30 : 15,
          width: isGrid ? 30 : 15
        };
      case 'audio':
        return {
          icon: 'play',
          height: isGrid ? 28 : 13,
          width: isGrid ? 28 : 13
        };
      default:
        return {
          icon: 'preview',
          height: isGrid ? 25 : 15,
          width: isGrid ? 25 : 15
        };
    }
  };

  return (
    <Wrapper ref={wrapperRef} grid={+!!isGrid}>
      {!isGrid ? (
        <Fade in={dimensions.height > 0 && previewDimensions?.width > 0} timeout={500}>
          <div>
            {dimensions.height > 0 && previewDimensions?.width > 0 && previewEnabledTypes.includes(data?.file_type) ? (
              <GrowablePreview height={dimensions.height} width={dimensions.width} url={urlToUse} type={data.file_type} expanded={+expanded}>
                {showPlayer && (
                  <Fade in timeout={1000}>
                    <div>{getPlayer()}</div>
                  </Fade>
                )}
                <div>
                  {!expanded && data.file_type !== 'image' && data.file_type !== 'video' && <PreviewIcon id="preview-icon" {...getMainIconProps()} />}
                  {!expanded && <LowerRightIcon {...getLowerRightIconProps()} />}
                </div>
              </GrowablePreview>
            ) : (
              <div>
                <PreviewIcon id={'preview-icon'} icon={data?.file_type === 'subtitle' ? 'ingest_subtitle' : 'ingest_doc'} height={34} width={34} />
              </div>
            )}
          </div>
        </Fade>
      ) : (
        <Fade in timeout={500}>
          <div onMouseEnter={() => setShowPlayer(true)} onMouseLeave={() => setShowPlayer(false)}>
            <GridPreview
              id="preview"
              type={data.file_type}
              url={data?.file_type === 'video' || data?.file_type === 'image' ? data?.thumbnail_url : signedUrl || ''}
            >
              {showPlayer && previewEnabledTypes.includes(data.file_type) && <PlayerWrapper>{getPlayer()}</PlayerWrapper>}
            </GridPreview>
            <div>
              {((!showPlayer && !['video', 'image'].includes(data.file_type)) || !['video', 'image', 'audio'].includes(data.file_type)) && (
                <PreviewIcon grid={+true} id="preview-icon" {...getMainIconProps()} />
              )}
            </div>
          </div>
        </Fade>
      )}
    </Wrapper>
  );
};

export default IngestMangementPreview;

const Wrapper = styled.div<{ grid: number }>`
  background: ${({ theme }) => darken(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.5 : 0.25)};
  height: 100%;
  width: 100%;
  position: relative;
  overflow: visible;
  cursor: ${({ grid }) => (grid ? 'pointer' : 'default')};
`;

const GrowablePreview = styled.div<{ height: number; width: number; url?: string; type: string; expanded: number }>`
  transition:
    height 0.3s,
    width 0.3s,
    opacity 0.3s;
  height: ${({ height }) => height - 1}px;
  width: ${({ width }) => width}px;
  position: absolute;
  top: 0px;
  left: 0;
  z-index: 2;
  overflow: hidden;

  &&::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: transform 0.3s ease-in-out;
    background: black;
    opacity: 1;
    z-index: -2;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: transform 0.3s ease-in-out;
    background: ${({ url, type, theme }) => {
      if (url && type === 'video') {
        return `url(${url}) center top / cover no-repeat`;
      } else if (url && type !== 'audio') {
        return `url(${url}) center center / cover no-repeat`;
      } else {
        return darken(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.5 : 0.25);
      }
    }};
    opacity: 0.5;
    z-index: -1;
  }

  &:hover::before {
    transform: scale(1.1);
  }
`;

const PreviewIcon = styled(PolarisIcon)<{ grid?: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  transition:
    height 0.3s,
    width 0.3s;
  color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.getContrastText(theme.palette.background.default) : theme.palette.grey[900]};
`;

const LowerRightIcon = styled(PolarisIcon)<{ grid?: true }>`
  position: absolute;
  bottom: ${({ theme, grid }) => (grid ? theme.spacing(1.4) : theme.spacing(1))};
  right: ${({ theme, grid }) => (grid ? theme.spacing(3) : theme.spacing(1))};
  z-index: 1;
  color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.getContrastText(theme.palette.background.default) : theme.palette.grey[800]};
`;

const PlayerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledAudioPlayer = styled(AudioPlayer)<{ height: number }>`
  min-height: ${({ height }) => height}px;
`;

const GridPreview = styled.div<{ url?: string; type: string }>`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio (9/16 * 100) */
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.3s;
  opacity: 0.7;

  background: ${({ url, type, theme }) => {
    if (url && type === 'video') {
      return `url(${url}) center top / cover no-repeat`;
    } else if (url && type !== 'audio') {
      return `url(${url}) center center / cover no-repeat`;
    } else {
      return darken(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.5 : 0.15);
    }
  }};

  &:hover {
    opacity: 1;
  }
`;
